import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { jobService } from '../../api/jobService';

const JobFormModal = ({ isOpen, onClose, job = null, onSuccess, isScheduled = false, mode = 'regular' }) => {
  const [loading, setLoading] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'STANDARD',
    priority: 'MEDIUM',
    price: '',
    scheduledDate: '', // Add scheduled date field
    driverId: '', // Add driver selection field
    route: '', // Add route field
    pickupLocation: {
      address: '',
      instructions: '',
      contactName: '',
      contactPhone: ''
    },
    intermediateStops: [],
    deliveryLocation: {
      address: '',
      instructions: '',
      contactName: '',
      contactPhone: ''
    },
    packageDetails: {
      weight: '',
      weightUnit: 'kg',
      type: 'REGULAR',
      description: '',
      specialInstructions: '',
      dimensions: ''
    },
    timing: {
      pickupDeadline: '',
      deliveryDeadline: '',
      estimatedDuration: '',
      estimatedTotalTime: ''
    },
    requirements: {
      vehicleType: 'CAR',
      driverRating: '',
      experienceLevel: 'BEGINNER'
    },
    compensation: {
      basePay: '',
      distanceBonus: '',
      urgencyBonus: ''
    },
    additional: {
      insurance: false,
      signatureRequired: false,
      paymentMethod: 'CREDIT_CARD'
    }
  });

  useEffect(() => {
    if (isOpen) {
      fetchAvailableDrivers();
      if (job) {
        console.log('Editing job:', job);
        console.log('Scheduled date from job:', job.scheduledDate);
        console.log('Scheduled date type:', typeof job.scheduledDate);
        if (job.scheduledDate && job.scheduledDate.toDate) {
          console.log('Scheduled date has toDate method:', job.scheduledDate.toDate());
        }
        
        // Handle both nested object structure and flat string structure
        const pickupAddress = typeof job.pickupLocation === 'string' 
          ? job.pickupLocation 
          : job.pickupLocation?.address || '';
        
        const deliveryAddress = typeof job.deliveryLocation === 'string' 
          ? job.deliveryLocation 
          : job.deliveryLocation?.address || '';

        setFormData({
          title: job.title || '',
          description: job.description || '',
          category: job.category || 'STANDARD',
          priority: job.priority || 'MEDIUM',
          price: job.price || '',
          scheduledDate: (() => {
            try {
              if (!job.scheduledDate) return '';
              
              // The backend already converts Firestore timestamps to ISO strings
              // So job.scheduledDate should already be an ISO string
              if (typeof job.scheduledDate === 'string') {
                // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:MM)
                const date = new Date(job.scheduledDate);
                if (isNaN(date.getTime())) {
                  console.warn('Invalid scheduled date string:', job.scheduledDate);
                  return '';
                }
                return date.toISOString().slice(0, 16);
              } else if (job.scheduledDate instanceof Date) {
                // Already a Date object
                return job.scheduledDate.toISOString().slice(0, 16);
              } else {
                // Fallback for other formats
                const date = new Date(job.scheduledDate);
                if (isNaN(date.getTime())) {
                  console.warn('Invalid scheduled date:', job.scheduledDate);
                  return '';
                }
                return date.toISOString().slice(0, 16);
              }
            } catch (error) {
              console.error('Error converting scheduled date:', error, job.scheduledDate);
              return '';
            }
          })(), // Set scheduled date
          driverId: job.driverId || '', // Set driver ID
          route: job.route || '', // Set route
          pickupLocation: {
            address: pickupAddress,
            instructions: job.pickupLocation?.instructions || '',
            contactName: job.pickupLocation?.contactName || job.customerName || '',
            contactPhone: job.pickupLocation?.contactPhone || job.customerPhone || ''
          },
          intermediateStops: job.intermediateStops || [],
          deliveryLocation: {
            address: deliveryAddress,
            instructions: job.deliveryLocation?.instructions || '',
            contactName: job.deliveryLocation?.contactName || '',
            contactPhone: job.deliveryLocation?.contactPhone || ''
          },
          packageDetails: {
            weight: job.weight ? job.weight.replace(/\s*\w+$/, '') : '', // Remove unit from weight string
            weightUnit: job.weight ? job.weight.match(/\w+$/)?.[0] || 'lbs' : 'lbs',
            type: job.packageDetails?.type || 'REGULAR',
            description: job.description || '',
            specialInstructions: job.specialInstructions || '',
            dimensions: job.dimensions || ''
          },
          timing: {
            pickupDeadline: job.pickupDeadline ? new Date(job.pickupDeadline).toISOString().slice(0, 16) : '',
            deliveryDeadline: job.deliveryDeadline ? new Date(job.deliveryDeadline).toISOString().slice(0, 16) : '',
            estimatedDuration: job.timeValue || job.estimatedTotalTime || '',
            estimatedTotalTime: job.estimatedTotalTime || ''
          },
          requirements: {
            vehicleType: job.requirements?.vehicleType || 'CAR',
            driverRating: job.requirements?.driverRating || '',
            experienceLevel: job.requirements?.experienceLevel || 'BEGINNER'
          },
          compensation: {
            basePay: job.basePay || '',
            distanceBonus: job.perMileRate || '',
            urgencyBonus: job.compensation?.urgencyBonus || ''
          },
          additional: {
            insurance: job.insurance || false,
            signatureRequired: job.signatureRequired || false,
            paymentMethod: job.paymentMethod || 'CREDIT_CARD'
          }
        });
        console.log('Form data set for editing');
        console.log('Final form data:', formData);
        console.log('Scheduled date in form data:', formData.scheduledDate);
      } else {
        // Reset form when creating new job
        setFormData({
          title: '',
          description: '',
          category: 'STANDARD',
          priority: 'MEDIUM',
          price: '',
          scheduledDate: '', // Reset scheduled date
          driverId: '', // Reset driver ID
          route: '', // Reset route
          pickupLocation: {
            address: '',
            instructions: '',
            contactName: '',
            contactPhone: ''
          },
          intermediateStops: [],
          deliveryLocation: {
            address: '',
            instructions: '',
            contactName: '',
            contactPhone: ''
          },
          packageDetails: {
            weight: '',
            weightUnit: 'kg',
            type: 'REGULAR',
            description: '',
            specialInstructions: '',
            dimensions: ''
          },
          timing: {
            pickupDeadline: '',
            deliveryDeadline: '',
            estimatedDuration: '',
            estimatedTotalTime: ''
          },
          requirements: {
            vehicleType: 'CAR',
            driverRating: '',
            experienceLevel: 'BEGINNER'
          },
          compensation: {
            basePay: '',
            distanceBonus: '',
            urgencyBonus: ''
          },
          additional: {
            insurance: false,
            signatureRequired: false,
            paymentMethod: 'CREDIT_CARD'
          }
        });
      }
    }
  }, [isOpen, job]);

  // Debug: Log form data changes
  useEffect(() => {
    console.log('Form data changed:', formData);
    console.log('Scheduled date in form data:', formData.scheduledDate);
  }, [formData]);

  const fetchAvailableDrivers = async () => {
    try {
      const response = await jobService.getAvailableDrivers();
      if (response.success) {
        setAvailableDrivers(response.data.drivers || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Helper functions for managing intermediate stops
  const addIntermediateStop = () => {
    setFormData(prev => ({
      ...prev,
      intermediateStops: [
        ...prev.intermediateStops,
        {
          id: Date.now(), // Simple unique ID
          storeNumber: ''
        }
      ]
    }));
  };

  const removeIntermediateStop = (stopId) => {
    setFormData(prev => ({
      ...prev,
      intermediateStops: prev.intermediateStops.filter(stop => stop.id !== stopId)
    }));
  };

  const updateIntermediateStop = (stopId, field, value) => {
    setFormData(prev => ({
      ...prev,
      intermediateStops: prev.intermediateStops.map(stop =>
        stop.id === stopId
          ? { ...stop, [field]: value }
          : stop
      )
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'STANDARD',
      priority: 'MEDIUM',
      price: '',
      scheduledDate: '',
      driverId: '',
      route: '',
      pickupLocation: {
        address: '',
        instructions: '',
        contactName: '',
        contactPhone: ''
      },
      intermediateStops: [],
      deliveryLocation: {
        address: '',
        instructions: '',
        contactName: '',
        contactPhone: ''
      },
      packageDetails: {
        weight: '',
        weightUnit: 'kg',
        type: 'REGULAR',
        description: '',
        specialInstructions: '',
        dimensions: ''
      },
      timing: {
        pickupDeadline: '',
        deliveryDeadline: '',
        estimatedDuration: '',
        estimatedTotalTime: ''
      },
      requirements: {
        vehicleType: 'CAR',
        driverRating: '',
        experienceLevel: 'BEGINNER'
      },
      compensation: {
        basePay: '',
        distanceBonus: '',
        urgencyBonus: ''
      },
      additional: {
        insurance: false,
        signatureRequired: false,
        paymentMethod: 'CREDIT_CARD'
      }
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title || !formData.pickupLocation.address || !formData.deliveryLocation.address || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Additional validation for scheduled jobs
    if (isScheduled) {
      if (!formData.scheduledDate) {
        toast.error('Please select a scheduled date for the job');
        return;
      }
      if (!formData.driverId) {
        toast.error('Please select a driver for the scheduled job');
        return;
      }
      
      // Validate that scheduled date is in the future
      const scheduledDate = new Date(formData.scheduledDate);
      const now = new Date();
      if (scheduledDate <= now) {
        toast.error('Scheduled date must be in the future');
        return;
      }
    }
    
    try {
      setLoading(true);
      
      // Transform form data to match backend expectations
      const jobData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        price: parseFloat(formData.price) || 0,
        scheduledDate: formData.scheduledDate, // Include scheduled date
        driverId: formData.driverId, // Include driver ID
        
        // Handle location data - convert to simple strings for backend
        pickupLocation: formData.pickupLocation.address,
        deliveryLocation: formData.deliveryLocation.address,
        
        // Intermediate stops data
        intermediateStops: formData.intermediateStops.map(stop => ({
          storeNumber: stop.storeNumber
        })),
        
        // Use manual route if provided, otherwise auto-generate
        route: formData.route || (formData.intermediateStops.length > 0 
          ? `${formData.pickupLocation.address} → ${formData.intermediateStops.map(stop => stop.storeNumber).join(' → ')} → ${formData.deliveryLocation.address}`
          : `${formData.pickupLocation.address} → ${formData.deliveryLocation.address}`),
        
        // Package details
        weight: formData.packageDetails?.weight && formData.packageDetails?.weightUnit ? `${formData.packageDetails.weight} ${formData.packageDetails.weightUnit}` : '0 lbs',
        specialInstructions: formData.packageDetails?.specialInstructions || '',
        dimensions: formData.packageDetails?.dimensions || '',
        
        // Timing
        estimatedDuration: formData.timing?.estimatedDuration ? parseInt(formData.timing.estimatedDuration) : 0,
        estimatedTotalTime: formData.timing?.estimatedTotalTime ? parseFloat(formData.timing.estimatedTotalTime) : 0,
        pickupDeadline: formData.timing?.pickupDeadline || null,
        deliveryDeadline: formData.timing?.deliveryDeadline || null,
        
        // Requirements
        requirements: {
          vehicleType: formData.requirements?.vehicleType || 'CAR',
          driverRating: formData.requirements?.driverRating ? parseFloat(formData.requirements.driverRating) : null,
          experienceLevel: formData.requirements?.experienceLevel || 'BEGINNER'
        },
        
        // Compensation
        basePay: formData.compensation?.basePay ? parseFloat(formData.compensation.basePay) : 0,
        perMileRate: formData.compensation?.distanceBonus ? parseFloat(formData.compensation.distanceBonus) : 0,
        
        // Additional requirements
        insurance: formData.additional?.insurance || false,
        signatureRequired: formData.additional?.signatureRequired || false,
        paymentMethod: formData.additional?.paymentMethod || 'CREDIT_CARD',
        
        // Additional fields for updates
        ...(job && {
          // Preserve existing fields that shouldn't be overwritten
          status: job.status,
          progress: job.progress,
          driverId: job.driverId,
          customerId: job.customerId,
          customerName: job.customerName,
          customerPhone: job.customerPhone,
          route: job.route,
          photos: job.photos || [],
          stagePhotos: job.stagePhotos || {},
          createdAt: job.createdAt,
        })
      };

      let response;
      
      // Handle different modes
      if (mode === 'predefined') {
        if (job) {
          // Update predefined job
          response = await jobService.updatePredefinedJob(job.id, jobData);
        } else {
          // Create predefined job
          response = await jobService.createPredefinedJob(jobData);
        }
      } else {
        // Regular job mode
        if (isScheduled) {
          if (job) {
            response = await jobService.updateScheduledJob(job.id, jobData);
          } else {
            response = await jobService.createScheduledJob(jobData);
          }
        } else {
          if (job) {
            response = await jobService.updateJob(job.id, jobData);
          } else {
            response = await jobService.createJob(jobData);
          }
        }
      }

      if (response.success) {
        toast.success(job ? 'Job updated successfully!' : 'Job created successfully!');
        if (onSuccess) {
          onSuccess(response.data);
        }
        handleClose();
      } else {
        toast.error(response.message || 'Failed to save job');
      }
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {job ? `Edit ${isScheduled ? 'Scheduled ' : ''}Job` : `Create New ${isScheduled ? 'Scheduled ' : ''}Job`}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Debug Section - Remove in production */}
          {job && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">Debug Info</h3>
              <div className="text-xs text-yellow-700 space-y-1">
                <p><strong>Job ID:</strong> {job.id}</p>
                <p><strong>Current Status:</strong> {job.status}</p>
                <p><strong>Current Progress:</strong> {job.progress}</p>
                <button
                  type="button"
                  onClick={() => console.log('Form Data:', formData)}
                  className="text-xs bg-yellow-200 hover:bg-yellow-300 px-2 py-1 rounded"
                >
                  Log Form Data
                </button>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter job title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="EXPRESS">Express</option>
                <option value="STANDARD">Standard</option>
                <option value="BULK">Bulk</option>
                <option value="FRAGILE">Fragile</option>
                <option value="FOOD">Food</option>
                <option value="DOCUMENTS">Documents</option>
                <option value="MEDICAL">Medical</option>
                <option value="GROCERIES">Groceries</option>
                <option value="FURNITURE">Furniture</option>
                <option value="ELECTRONICS">Electronics</option>
                <option value="CLOTHING">Clothing</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter job description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <select
                required
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ($) *
              </label>
              <input
                type="number"
                required
                min="5"
                max="1000"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            {isScheduled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled Date *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.scheduledDate || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Driver *
                  </label>
                  <select
                    required
                    value={formData.driverId}
                    onChange={(e) => setFormData(prev => ({ ...prev, driverId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a driver</option>
                    {availableDrivers.map(driver => (
                      <option key={driver.id} value={driver.id}>
                        {driver.firstName} {driver.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                value={formData.timing?.estimatedDuration || ''}
                onChange={(e) => handleInputChange('timing', 'estimatedDuration', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="120"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Total Time (hours)
              </label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={formData.timing.estimatedTotalTime || ''}
                onChange={(e) => handleInputChange('timing', 'estimatedTotalTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="2.5"
              />
            </div>
          </div>

          {/* Pickup Location */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pickup Location</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pickup Address *
                </label>
                <input
                  type="text"
                  required
                  value={formData.pickupLocation?.address || ''}
                  onChange={(e) => handleInputChange('pickupLocation', 'address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter pickup address"
                />
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.pickupLocation.contactName || ''}
                  onChange={(e) => handleInputChange('pickupLocation', 'contactName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Contact person name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.pickupLocation.contactPhone || ''}
                  onChange={(e) => handleInputChange('pickupLocation', 'contactPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1234567890"
                />
              </div> */}
              {/* <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  value={formData.pickupLocation.instructions || ''}
                  onChange={(e) => handleInputChange('pickupLocation', 'instructions', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Special pickup instructions"
                />
              </div> */}
            </div>
          </div>

          {/* Intermediate Stops */}
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Intermediate Stops</h3>
              <button
                type="button"
                onClick={addIntermediateStop}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Stop
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Add intermediate stops between pickup and delivery. Each stop represents a store location where packages will be picked up.
            </p>

            {formData.intermediateStops.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No intermediate stops</h3>
                <p className="mt-1 text-sm text-gray-500">Click "Add Stop" to add store locations between pickup and delivery.</p>
              </div>
            )}

            {formData.intermediateStops.map((stop, index) => (
              <div key={stop.id} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-medium text-gray-900">
                    Stop #{index + 1} - Store Location
                  </h4>
                  <button
                    type="button"
                    onClick={() => removeIntermediateStop(stop.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove Stop
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Store Number *
                    </label>
                    <input
                      type="text"
                      required
                      value={stop.storeNumber || ''}
                      onChange={(e) => updateIntermediateStop(stop.id, 'storeNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., STORE-001, WAL-123, TARGET-456"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            {/* Route Preview */}
            {formData.intermediateStops.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Route Preview</h4>
                <p className="text-sm text-blue-700">
                  {formData.pickupLocation.address} → {formData.intermediateStops.map(stop => stop.storeNumber).join(' → ')} → {formData.deliveryLocation.address}
                </p>
              </div>
            )}
            
            {/* Manual Route Override */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Route (Optional - Auto-generated if empty)
              </label>
              <input
                type="text"
                value={formData.route}
              
                onChange={(e) => setFormData(prev => ({ ...prev, route: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Leave empty for auto-generation or enter custom route"
              />
            </div>
          </div>

          {/* Delivery Location */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Location</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Address *
                </label>
                <input
                  type="text"
                  required
                  value={formData.deliveryLocation.address || ''}
                  onChange={(e) => handleInputChange('deliveryLocation', 'address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter delivery address"
                />
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.deliveryLocation.contactName || ''}
                  onChange={(e) => handleInputChange('deliveryLocation', 'contactName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Contact person name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.deliveryLocation.contactPhone || ''}
                  onChange={(e) => handleInputChange('deliveryLocation', 'contactPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1234567890"
                />
              </div> */}
              {/* <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  value={formData.deliveryLocation.instructions || ''}
                  onChange={(e) => handleInputChange('deliveryLocation', 'instructions', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Special delivery instructions"
                />
              </div> */}
            </div>
          </div>

          {/* Package Details */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Package Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight *
                </label>
                <input
                  type="number"
                  required
                  min="0.1"
                  max="500"
                  step="0.1"
                  value={formData.packageDetails?.weight || ''}
                  onChange={(e) => handleInputChange('packageDetails', 'weight', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="5.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight Unit
                </label>
                <select
                  value={formData.packageDetails?.weightUnit || 'kg'}
                  onChange={(e) => handleInputChange('packageDetails', 'weightUnit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="kg">Kilograms (kg)</option>
                  <option value="lbs">Pounds (lbs)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package Type
                </label>
                <select
                  value={formData.packageDetails?.type || 'REGULAR'}
                  onChange={(e) => handleInputChange('packageDetails', 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="REGULAR">Regular</option>
                  <option value="FRAGILE">Fragile</option>
                  <option value="PERISHABLE">Perishable</option>
                  <option value="HAZARDOUS">Hazardous</option>
                  <option value="LARGE">Large</option>
                  <option value="HEAVY">Heavy</option>
                  <option value="DOCUMENTS">Documents</option>
                  <option value="ELECTRONICS">Electronics</option>
                  <option value="FOOD">Food</option>
                  <option value="MEDICAL">Medical</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Description
              </label>
              <textarea
                value={formData.packageDetails?.description || ''}
                onChange={(e) => handleInputChange('packageDetails', 'description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the package contents"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Dimensions
              </label>
              <input
                type="text"
                value={formData.packageDetails?.dimensions || ''}
                onChange={(e) => handleInputChange('packageDetails', 'dimensions', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 12x8x6 inches, 30x20x15 cm"
              />
            </div>
            {/* <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Instructions
              </label>
              <textarea
                value={formData.packageDetails?.specialInstructions || ''}
                onChange={(e) => handleInputChange('packageDetails', 'specialInstructions', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Special handling instructions"
              />
            </div> */}
          </div>

          {/* Timing */}
          {/* <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Timing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pickup Deadline
                </label>
                <input
                  type="datetime-local"
                  value={formData.timing?.pickupDeadline || ''}
                  onChange={(e) => handleInputChange('timing', 'pickupDeadline', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Deadline
                </label>
                <input
                  type="datetime-local"
                  value={formData.timing?.deliveryDeadline || ''}
                  onChange={(e) => handleInputChange('timing', 'deliveryDeadline', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div> */}

          {/* Requirements */}
          {/* <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle Type
                </label>
                <select
                  value={formData.requirements?.vehicleType || 'CAR'}
                  onChange={(e) => handleInputChange('requirements', 'vehicleType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="BIKE">Bike</option>
                  <option value="MOTORCYCLE">Motorcycle</option>
                  <option value="CAR">Car</option>
                  <option value="VAN">Van</option>
                  <option value="TRUCK">Truck</option>
                  <option value="SPECIALIZED">Specialized</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Driver Rating
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={formData.requirements?.driverRating || ''}
                  onChange={(e) => handleInputChange('requirements', 'driverRating', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="4.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level
                </label>
                <select
                  value={formData.requirements?.experienceLevel || 'BEGINNER'}
                  onChange={(e) => handleInputChange('requirements', 'experienceLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="BEGINNER">Beginner</option>
                  <option value="INTERMEDIATE">Intermediate</option>
                  <option value="EXPERT">Expert</option>
                </select>
              </div>
            </div>
          </div> */}

          {/* Additional Requirements */}
          {/* <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <select
                  value={formData.additional?.paymentMethod || 'CREDIT_CARD'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    additional: { ...prev.additional, paymentMethod: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="CREDIT_CARD">Credit Card</option>
                  <option value="DEBIT_CARD">Debit Card</option>
                  <option value="CASH">Cash</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="DIGITAL_WALLET">Digital Wallet</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="insurance"
                  checked={formData.additional?.insurance || false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    additional: { ...prev.additional, insurance: e.target.checked }
                  }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="insurance" className="ml-2 block text-sm text-gray-700">
                  Insurance Required
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="signatureRequired"
                  checked={formData.additional?.signatureRequired || false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    additional: { ...prev.additional, signatureRequired: e.target.checked }
                  }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="signatureRequired" className="ml-2 block text-sm text-gray-700">
                  Signature Required
                </label>
              </div>
            </div>
          </div> */}

          {/* Compensation */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Compensation (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Pay ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.compensation?.basePay || ''}
                  onChange={(e) => handleInputChange('compensation', 'basePay', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="35.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance Bonus ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.compensation?.distanceBonus || ''}
                  onChange={(e) => handleInputChange('compensation', 'distanceBonus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="10.50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Urgency Bonus ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.compensation?.urgencyBonus || ''}
                  onChange={(e) => handleInputChange('compensation', 'urgencyBonus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="5.00"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="border-t pt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave className="mr-2" />
                  {job ? `Update ${mode === 'predefined' ? 'Predefined Job' : 'Job'}` : `Create ${mode === 'predefined' ? 'Predefined Job' : 'Job'}`}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobFormModal; 